import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import _ from 'underscore';
import { i18nKeys } from '@bingads-webui-react/file-picker';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input, Select } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { CustomValidateItems } from '../validator';
import { TaxTypeFormPropTypes } from '../shapes';
import { InputTaxFieldId, FileScenarioMapping, requiredFeatures } from '../constants';
import { registerTaxform } from '../taxform-store';
import { TaxCertificateFilePicker } from '../controls';
import { withViewTaxFormWithFilledEmptyTaxValue, withTaxFormByPilot, getHelpPopupTopicFromTaxSchema, shouldDisplayTaxCertifications, getTaxTypeLabel } from '../utils';
import { EmptyTaxForm } from './empty-taxform';
import { COViewTaxForm } from './viewtaxforms/colombia-viewtaxform';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  TaxLabelFormat, TaxType, VatStatusEnum, TaxExemptionCertVerifiedResult, CountryCode,
} = Constants;
const {
  isCOVatNumberValid, normalizeTaxExemptionCertVerifiedResult,
} = TaxUtils;

const getCOTaxSchema = () => ({
  name: 'Colombia Taxation Validation Schema',
  type: 'object',
  properties: {
    [TaxType.VatNumber]: {
      type: 'string',
      required: false,
      helpPopupTopic: 'Pop_BA_Accounts_NITNumber_CO',
    },
    IsTaxExemptionCertVerified: {
      // type is not bool because we have 3 status: Approve('True'), Reject('False'), Not set(null).
      // We use the `convertor` on the Select component to parse the string to the optional(bool).
      type: 'string',
    },
  },
});

export const COTaxForm = ({
  i18n,
  onValidate,
  showLineByLineFormGroup,
  alwaysShowError,
  data,
  mtData,
  taxCertification,
  mtTaxCertification,
  showTaxApprovalForm,
  disabled,
  urlBuilder,
  showTaxCertLink,
}) => {
  const validationI18n = useValidationI18n(i18n);
  const customStrings = useMemo(() => ({
    [i18nKeys.FilePicker_ErrorMessage_Required]: i18n.getString(_TL_('Please upload your tax certificate/RUT document associated with your VAT ID.')),
  }), [i18n]);
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const { taxCertifications: prefilledTaxCertifications } = taxCertification;
  const prefilledDisplayCertifications = shouldDisplayTaxCertifications(prefilledTaxCertifications) ? prefilledTaxCertifications : [];
  const taxSchema = getCOTaxSchema();

  const [certificationsState, setCertificationsState] = useState({
    certifications: [],
    isValid: false,
  });

  const [taxData, setTaxData] = useState(data);

  const additionalFormData = useMemo(() => ({ scenario: FileScenarioMapping.CO }), []);
  const hasVatNumber = !_.isEmpty(taxData.VatNumber);
  const isTaxDataRequired = !_.isEmpty(certificationsState.certifications);
  const isCertRequired = hasVatNumber && taxData.VatNumber !== mtData.VatNumber;
  const isTaxDataValid = isCOVatNumberValid(taxData.VatNumber) || (!isTaxDataRequired && _.isEmpty(taxData.VatNumber));
  const isTaxCertPendingReview = mtTaxCertification.status === VatStatusEnum.Pending;

  const onTaxFormValidate = useCallback(({ data: vatTaxData }) => {
    setTaxData(vatTaxData);
  }, []);

  const onCertificationsChange = useCallback(({ certifications, isValid }) => {
    setCertificationsState({
      certifications,
      isValid,
    });
  }, []);

  useEffect(() => {
    const taxCertifications = _.isEmpty(certificationsState.certifications) ? mtTaxCertification.taxCertifications : certificationsState.certifications;
    const isSuccessfullyUploaded = _.every(taxCertifications, cert => cert && cert.guid);
    const isValid = isTaxDataValid && certificationsState.isValid && isSuccessfullyUploaded;

    onValidate({
      data: taxData,
      taxCertification: {
        taxCertifications,
      },
      isValid,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificationsState, taxData, isTaxDataValid]);

  // Clear all data when unmount
  // useLayoutEffect to make sure it runs before the next component's effect
  useLayoutEffect(() => () => onValidate({
    data: {},
    taxCertification: null,
    isValid: true,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <div>
      <ValidationGroup
        validationProps={{
          i18n: validationI18n,
          data,
          onValidate: onTaxFormValidate,
          schema: taxSchema,
        }}
      >
        <CustomizedFormGroup
          isStatic
          label={getTaxTypeLabel(TaxType.VatNumber, i18n, { labelFormat: TaxLabelFormat.RequiredForValueAddedTaxIVARegistered, countryCode: CountryCode.CO })}
          helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.VatNumber })}
          className="tax-data-input"
        >
          <Input
            id={InputTaxFieldId(TaxType.VatNumber)}
            disabled={disabled}
            validationProps={{
              field: TaxType.VatNumber,
              alwaysShowError,
              showErrorOnPassiveMessages: true,
              passiveMessages: isTaxDataValid
                ? []
                : [i18n.getString(hasVatNumber ? CustomValidateItems.ColombiaVATFormatValidation : CustomValidateItems.ColombiaVatNumberValidation)],
            }}
          />
        </CustomizedFormGroup>
        <TaxCertificateFilePicker
          i18n={i18n}
          customStrings={customStrings}
          certifications={prefilledDisplayCertifications}
          mtTaxCertification={mtTaxCertification}
          onCertificationsChange={onCertificationsChange}
          showLineByLineFormGroup={showLineByLineFormGroup}
          alwaysShowError={alwaysShowError || hasVatNumber}
          disabled={disabled}
          required={isCertRequired}
          urlBuilder={urlBuilder}
          showTaxCertLink={showTaxCertLink}
          taxCertLabel={i18n.getString(_TL_('Tax certificate/RUT document'))}
          additionalFormData={additionalFormData}
        />
        <InlineInfoAlert>{i18n.getString(_TL_('Failure to provide your NIT/TAX ID and Tax Certificate may result in tax being applied to your invoice.'))}</InlineInfoAlert>
        {
          showTaxApprovalForm && (
            <CustomizedFormGroup
              isStatic
              label={i18n.getString(_TL_('Tax certificate validation result'))}
            >
              <Select
                id="TaxCertificationValidationResult"
                disabled={disabled || !isTaxCertPendingReview}
                validationProps={{
                  field: 'IsTaxExemptionCertVerified',
                  alwaysShowError,
                  convertor: {
                    // NotSet value is null in backend, option only accepts strings, doing a mapping here
                    fromModel: value => normalizeTaxExemptionCertVerifiedResult(value) || 'NotSet',
                    toModel: normalizeTaxExemptionCertVerifiedResult,
                  },
                }}
              >
                <option value="NotSet">{i18n.getString(_TL_('(Not set)'))}</option>
                <option value={TaxExemptionCertVerifiedResult.Accept}>{i18n.getString(_TL_('Approve'))}</option>
                <option value={TaxExemptionCertVerifiedResult.Reject}>{i18n.getString(_TL_('Reject'))}</option>
              </Select>
            </CustomizedFormGroup>)
        }
      </ValidationGroup>
    </div>
  );
};

COTaxForm.propTypes = TaxTypeFormPropTypes;

const pilotFlag = requiredFeatures.ABLColombiaTax;
const TaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: COTaxForm, DefaultForm: EmptyTaxForm });
const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: COViewTaxForm, DefaultForm: EmptyViewTaxForm });

registerTaxform('/CO/', TaxForm, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: getCOTaxSchema }));
